export const SUGGESTED_PROMPTS: {
  path: string;
  feature: string;
  overview: string;
  description: string;
  prompts: string[];
}[] = [
  {
    path: '/partners/pending-requests',
    feature: '[Partners] Partner Requests',
    overview:
      'A workflow for sending, receiving, and managing invitations to connect and collaborate with new partners.',
    description:
      'What users do: Send partnership invites to new companies, accept or reject incoming requests, and onboard new partners into their ecosystem.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/partners/sharing-dashboard',
    feature: '[Partners] Sharing Dashboard',
    overview:
      'An interface that summarizes what data is being shared with which partners and controls the sharing settings.',
    description:
      'What users do: Monitor and adjust data sharing settings by population or field, review overlap activity, and ensure compliance with data-sharing policies.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/partners',
    feature: '[Partners] Partner List',
    overview:
      'A centralized view where users can see all organizations their company is partnered with in Crossbeam.',
    description:
      'What users do: Browse, search, and manage partner connections, quickly review data sharing status, and initiate co-selling or joint activities.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/reports/account-mapping',
    feature: '[Mapping] Create a Report',
    overview:
      'Lets users build custom views to analyze account or lead overlaps with partners, applying filters and selecting specific data points.',
    description:
      'What users do: Define criteria and partners, filter by segments, and generate actionable overlap reports to inform co-selling, prospecting, or account planning.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/reports',
    feature: '[Mapping] Saved Reports',
    overview:
      'Stores previously created reports for quick access, re-use, and sharing with teammates or stakeholders.',
    description:
      'What users do: Instantly revisit frequently used report setups, monitor evolving overlap data, and collaborate by sharing key reports internally.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/co-sell/requests',
    feature: '[Collaborate] Messages',
    overview:
      'Provides a communication tool within Crossbeam for users to directly contact partners about shared overlaps or co-selling opportunities.',
    description:
      'What users do: Initiate conversations around specific overlaps, request warm introductions, share intel, and track correspondence for partnership activities.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/collaborate',
    feature: '[Collaborate] Shared Lists',
    overview:
      'Enables users to create and share curated lists of accounts or leads with partners, driving targeted collaboration.',
    description:
      'What users do: Build and send custom account or lead lists, focus joint activities on strategic targets, and align go-to-market outreach with partners.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/attribution',
    feature: 'Attribution',
    overview:
      'Tracks and surfaces which partners or partner activities have influenced pipeline creation or revenue outcomes within your CRM.',
    description:
      'What users do: View which deals originated or progressed due to partner involvement, justify partner value, and inform future partnership and co-selling strategy.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/deal-navigator',
    feature: 'Deal Navigator',
    overview:
      'A workspace to manage ecosystem-influenced deals, see partner overlap details, and take action directly from each opportunity or account.',
    description:
      'What users do: Prioritize deals with strong partner signals, review partner-provided context, connect with partner reps, and accelerate co-selling workflows.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/measure/ecosystem',
    feature: 'Performance',
    overview:
      'Provides analytics and summary metrics on partner ecosystem activity, overlap trends, and impact on pipeline and revenue.',
    description:
      'What users do: Monitor partnership program health, track pipeline influenced by partners over time, identify top-performing partners, and optimize ecosystem GTM.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/data-sources',
    feature: '[Data] Data Sources',
    overview:
      'Connects Crossbeam to external systems like CRMs or spreadsheets to import account, lead, and opportunity data.',
    description:
      'What users do: Link their Salesforce, HubSpot, Snowflake, CSVs, etc., so that Crossbeam stays up-to-date with their latest customer and prospect records.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/populations',
    feature: '[Data] Populations',
    overview:
      'Customizable segments of data—such as “Customers,” “Prospects,” or “Open Opportunities”—used to control what is shared and compared with partners.',
    description:
      'What users do: Define and manage groups of accounts or leads, select which populations to include in partner overlaps, and tailor collaboration strategies.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/default-sharing',
    feature: '[Data] Default Sharing',
    overview:
      'Pre-set rules that determine what data is automatically shared with each new partner upon connection.',
    description:
      'What users do: Configure default sharing permissions by population or data field to speed up onboarding while ensuring internal data policies are met.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
  {
    path: '/integrations',
    feature: '[Data] Integrations',
    overview:
      'Enables seamless interoperability between Crossbeam and external platforms (CRM, Slack, Zapier, etc.) to automate workflows and sync data.',
    description:
      'What users do: Activate integrations to push overlap intelligence into other tools, automate ecosystem workflows, and streamline reporting and productivity.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
    ],
  },
];

export const DEFAULT_SUGGESTED_PROMPTS = [
  'Which partners do we share the most overlaps with?',
  'Who are my top 5 partners by potential revenue?',
  'Who are my 3 newest partners?',
];

export const AI_CHAT_MODES = {
  WINDOW: 'window',
  DRAWER: 'drawer',
} as const;
